import { constant } from 'underscore';
import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from '@xstate/react';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { ProgressiveLoadingBlock } from '@bingads-webui-campaign-react/shell-ui-common';
import {
  StepEvents,
  isLoading as isLoadingSelector,
  isFailedInit as isFailedInitSelector,
  isFin as isFinSelector,
  isStopping as sharedIsStoppingSelector,
  log,
  stateLoggingListener,
} from '@bingads-webui-campaign-react/step-wizard-machine';
import { useUpdateChildPerfMarker } from '@bingads-webui-campaign-react/performance-marker-provider';

export const StepLoadingStatus = {
  loading: 'loading',
  ready: 'ready',
  failed: 'failed',
};

export const defaultLoadingStatusSelector = (state) => {
  if (isFailedInitSelector(state)) {
    return StepLoadingStatus.failed;
  }

  if (isLoadingSelector(state)) {
    return StepLoadingStatus.loading;
  }

  return StepLoadingStatus.ready;
};

export const defaultIsStoppingSelector = state => sharedIsStoppingSelector(state) || isFinSelector(state);

export const defaultFallback = () => (<ProgressiveLoadingBlock />);

/**
 * The step state machine-aware higher-order React component for wrapping an actual step UI component.
 * The HOC will render the provided fallback UI until the step has loaded initial data
 * required for it to render.
 */
const StepLoadingShell = React.memo(({
  stepService,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  loadingStatusSelector,
  isStoppingSelector,
  stepComponent: StepComponent,
  fallback: Fallback,
  wizardPerfMarker,
  stepPerfMarkerName,
  ...restProps
}) => {
  useEffect(() => {
    const subscription = stepService.subscribe(stateLoggingListener);
    log('StepLoadingShell mounted');
    return () => {
      log('StepLoadingShell unmounted');
      subscription.unsubscribe();
    };
  }, [stepService]);

  const isLoading = useSelector(stepService, isLoadingSelector);
  const isStopping = useSelector(stepService, isStoppingSelector);
  const perfmarker = useMemo(
    () => {
      const child = wizardPerfMarker.createChild(stepPerfMarkerName, { autoEnd: false });
      child.willInit();
      return child;
    },
    [wizardPerfMarker, stepPerfMarkerName]
  );

  useUpdateChildPerfMarker(perfmarker, isLoading);

  useEffect(() => {
    stepService.send({ type: StepEvents.OVERRIDE_GUARDS, data: { needsStopping: constant(true) } });
  }, [stepService]);

  useEffect(() => {
    if (isStopping) {
      log('StepLoadingShell sending DONE_STOPPING');
      stepService.send({ type: StepEvents.DONE_STOPPING });
    }
  }, [stepService, isStopping]);

  log('StepLoadingShell.render: isLoading, isStopping', isLoading, isStopping);

  if (isLoading) {
    return <Fallback stepService={stepService} wizardPerfMarker={perfmarker} {...restProps} />;
  } else if (!isStopping) {
    return <StepComponent stepService={stepService} wizardPerfMarker={perfmarker} {...restProps} />;
  }
  return null;
});

StepLoadingShell.propTypes = {
  stepService: PropTypes.shape({
    subscribe: PropTypes.func.isRequired,
    send: PropTypes.func,
  }).isRequired,
  stepComponent: PropTypes.elementType.isRequired,
  loadingStatusSelector: PropTypes.func,
  isStoppingSelector: PropTypes.func,
  fallback: PropTypes.elementType,
  wizardPerfMarker: PropTypes.shape({
    createChild: PropTypes.func.isRequired,
  }).isRequired,
  stepPerfMarkerName: PropTypes.string.isRequired,
};

StepLoadingShell.defaultProps = {
  loadingStatusSelector: defaultLoadingStatusSelector,
  isStoppingSelector: defaultIsStoppingSelector,
  fallback: defaultFallback,
};

export const withStepLoadingShell = (
  StepComponent,
  {
    loadingStatusSelector: staticLoadingStatusSelector,
    isStoppingSelector: staticIsStoppingSelector,
    fallback: staticFallback,
  } = {}
) => {
  const HOC = ({
    stepService, loadingStatusSelector, isStoppingSelector, fallback, wizardPerfMarker, stepPerfMarkerName, ...restProps
  }) => (
    <StepLoadingShell
      stepService={stepService}
      stepComponent={StepComponent}
      loadingStatusSelector={loadingStatusSelector || staticLoadingStatusSelector}
      isStoppingSelector={isStoppingSelector || staticIsStoppingSelector}
      fallback={fallback || staticFallback}
      wizardPerfMarker={wizardPerfMarker}
      stepPerfMarkerName={stepPerfMarkerName}
      {...restProps}
    />
  );

  HOC.propTypes = {
    stepService: PropTypes.shape({}).isRequired,
    loadingStatusSelector: PropTypes.func,
    isStoppingSelector: PropTypes.func,
    fallback: PropTypes.elementType,
    wizardPerfMarker: PropTypes.shape({
      createChild: PropTypes.func.isRequired,
    }).isRequired,
    stepPerfMarkerName: PropTypes.string.isRequired,
  };

  HOC.defaultProps = {
    loadingStatusSelector: undefined,
    isStoppingSelector: undefined,
    fallback: undefined,
  };

  HOC.displayName = `withStepLoadingShell(${getDisplayName(StepComponent)})`;
  hoistNonReactStatics(HOC, StepComponent);

  return HOC;
};
